import type { SalesOpportunity } from "@color-finder/color-finder-api/models";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Autocomplete, Link, TextField, Typography } from "@mui/material";
import type { StandardTextFieldProps } from "@mui/material/TextField/TextField";
import { type Dispatch, type FC, useState } from "react";
import { useAppConfig } from "../../../config/app-config";
import { useHistorySettings } from "../../../hooks/history-settings";
import { useOpportunities } from "../../../hooks/opportunities";
import { useDebounce } from "../../../hooks/utils/debounce";
import { AutocompleteErrorMessage } from "../colorSearchAutocomplete/AutocompleteErrorMessage";

export interface OpportunityAutocompleteProps
  extends Omit<StandardTextFieldProps, "value" | "onChange"> {
  value?: SalesOpportunity;
  onChange?: Dispatch<SalesOpportunity | undefined>;
}

export const OpportunityAutocomplete: FC<OpportunityAutocompleteProps> = ({
  value,
  onChange,
  ...textFieldProps
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string>();
  const [appConfig] = useAppConfig();
  const debouncedQuery = useDebounce(
    query,
    appConfig.ux.general.inputDebounceTimeMs,
  );
  const [options, error, isLoading] = useOpportunities(debouncedQuery, 20);

  const [setting, setSettings] = useHistorySettings<SalesOpportunity>(
    "opportunityAutocomplete",
  );

  const onValueChange = (value: SalesOpportunity | undefined) => {
    onChange?.(value);
    if (value) {
      setSettings({
        time: new Date(),
        value: value,
      });
    }
  };

  const combinedOptions:
    | Array<SalesOpportunity & { group?: string }>
    | undefined = options
    ? [
        ...setting.map((s) => ({ ...s.value, group: "Recently used" })),
        ...options.map((o) => ({ ...o, group: "Results" })),
      ]
    : [];

  return (
    <>
      <Autocomplete
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            {...textFieldProps}
            label="C4C Opportunity ID"
          />
        )}
        renderOption={(props, option: SalesOpportunity) => (
          <li {...props}>
            <Typography fontSize="inherit">{option.id}</Typography>
            <Typography
              fontSize="var(--body-b3-12-regular-size, 12px)"
              color="text.secondary"
              fontStyle="italic"
              sx={{ ml: "auto" }}
            >
              {option.label}
            </Typography>
          </li>
        )}
        getOptionLabel={(o) => o?.id}
        value={value ?? null}
        loading={isLoading}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={(_, item) => onValueChange(item ?? undefined)}
        onInputChange={(_e, value) => setQuery(value)}
        options={combinedOptions}
        groupBy={(o) => (o as { group?: string }).group ?? ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={
          error ? <AutocompleteErrorMessage error={error} /> : undefined
        }
      />
      <Typography
        mb={0.5}
        mx={0.5}
        fontSize="var(--body-b2-14-regular-size, 14px)"
      >
        <Link
          href={appConfig.salesOpportunities.searchOpportunityUrl}
          target="_blank"
          underline="hover"
        >
          Search Opportunities in SAP Cloud for Customer{" "}
          <OpenInNewIcon sx={{ fontSize: 14, mb: "-2px" }} />
        </Link>
      </Typography>
    </>
  );
};
